// middleware/generalApi.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const generalApi = axios.create({
  // baseURL: "https://localhost:5000/api", // Base route is now /api instead of /api/srs
  baseURL: "https://api.ankiverse.com/api",

  withCredentials: true,
});

// Helper: Decode token and return its expiration time (in ms)
function getTokenExpiry(token) {
  try {
    const decoded = jwtDecode(token);
    // JWT exp is in seconds; convert to ms
    return decoded.exp * 1000;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

let refreshTimeout;

// Schedule a proactive token refresh before expiry
function scheduleRefresh() {
  const token = localStorage.getItem("accessToken");
  if (!token) return;
  const expiryTime = getTokenExpiry(token);
  if (!expiryTime) return;

  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;
  // Refresh 1 minute (60000ms) before expiry
  const refreshDelay = timeUntilExpiry - 60000;

  // If refreshDelay is negative, refresh immediately
  if (refreshDelay <= 0) {
    refreshAccessToken();
  } else {
    // Clear any existing timeout and schedule a new one
    if (refreshTimeout) clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(refreshAccessToken, refreshDelay);
  }
}

// Function to refresh the access token
async function refreshAccessToken() {
  try {
    const { data } = await axios.post(
      "https://localhost:5000/api/auth/refresh-token",
      {},
      { withCredentials: true }
    );
    localStorage.setItem("accessToken", data.accessToken);
    generalApi.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
    // Reschedule the proactive refresh with the new token
    scheduleRefresh();
  } catch (err) {
    console.error("Proactive token refresh failed", err);
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  }
}

// Request interceptor: attach the access token to every request
generalApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: if a 401 error occurs, attempt a token refresh once
generalApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Check that error.response exists before trying to access status
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post(
          "https://localhost:5000/api/auth/refresh-token",
          {},
          { withCredentials: true }
        );
        localStorage.setItem("accessToken", data.accessToken);
        generalApi.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
        // Reschedule proactive refresh after the token update
        scheduleRefresh();
        return generalApi(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// If an access token exists on load, schedule its refresh
if (localStorage.getItem("accessToken")) {
  scheduleRefresh();
}

export default generalApi;